import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth, api } from '../contexts/AuthContext';
import { downloadFromResponse } from '../utils/download';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  Plus, 
  Upload,
  FolderOpen,
  Clock,
  MoreVertical,
  Trash2,
  LogOut,
  User,
  ArrowRight,
  File,
  Loader2,
  Settings,
  Download,
  HelpCircle,
  Gift,
  BarChart3,
  FileText,
  Scissors,
  CheckCircle,
  AlertCircle,
  Play,
  Sparkles,
  Coins,
  Brush,
  XCircle,
  ExternalLink,
  Target,
  Users,
  CloudUpload,
  Monitor
} from 'lucide-react';
import SupportButton from '../components/SupportButton';
import ReferralModal from '../components/ReferralModal';
import CreditsBadge from '../components/CreditsBadge';
import OnboardingVideoModal, { useOnboarding } from '../components/OnboardingVideoModal';
import ExperienceSurveyModal from '../components/ExperienceSurveyModal';

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      // Check screen width and touch capability
      const mobileWidth = window.innerWidth < 768;
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(mobileWidth || (hasTouchScreen && window.innerWidth < 1024));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Data hygiene tips to show during upload
const DATA_HYGIENE_TIPS = [
  "Companies with clean CRM data see 66% higher email deliverability.",
  "Dirty data costs US businesses $3.1 trillion annually.",
  "Sales teams waste 27% of their time on bad data quality.",
  "Clean data improves marketing ROI by up to 40%.",
  "70% of CRM data becomes outdated within a year.",
  "Duplicate records can inflate marketing costs by 25%.",
  "Data quality issues cause 40% of business initiatives to fail.",
  "Clean data = cleaner segmentation = better targeting = more revenue.",
  "Companies with clean data are 5x more likely to make faster decisions.",
  "Poor data quality reduces productivity by an average of 20%."
];

const DataHygieneTip = () => {
  const [tipIndex, setTipIndex] = useState(Math.floor(Math.random() * DATA_HYGIENE_TIPS.length));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % DATA_HYGIENE_TIPS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <p className="text-xs text-indigo-700 italic transition-opacity">
      {DATA_HYGIENE_TIPS[tipIndex]}
    </p>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  
  // Mobile detection - file upload is blocked on mobile
  const isMobile = useIsMobile();
  
  // Onboarding modal
  const { showOnboarding, openOnboarding, closeOnboarding } = useOnboarding();
  const [aggregateMetrics, setAggregateMetrics] = useState(null);
  
  // Experience survey (shown first for new users)
  const [showExperienceSurvey, setShowExperienceSurvey] = useState(false);
  const [experienceSurveyCompleted, setExperienceSurveyCompleted] = useState(false);
  
  // Upload glow state (after onboarding complete)
  const [showUploadGlow, setShowUploadGlow] = useState(false);
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    fetchProjects();
    checkAdminStatus();
    checkIfNewUser();
  }, []);
  
  // Show upload glow for new users - skip all onboarding/surveys
  useEffect(() => {
    if (user && !user.onboarding_completed) {
      // Just show upload glow directly
      setShowUploadGlow(true);
      // Remove glow after 10 seconds
      setTimeout(() => setShowUploadGlow(false), 10000);
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      const statusCode = error.response?.status;
      if (statusCode === 520 || statusCode === 502 || statusCode === 503 || statusCode === 504) {
        toast.error('Server temporarily busy. Please refresh the page.', { duration: 5000 });
      } else {
        toast.error('Failed to load projects. Please refresh to try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await api.get('/api/auth/check-admin');
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const checkIfNewUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setIsNewUser(response.data.is_new_user || false);
      // Store aggregate metrics for Time Saved Meter
      if (response.data.aggregate_metrics) {
        setAggregateMetrics(response.data.aggregate_metrics);
      }
    } catch (error) {
      setIsNewUser(false);
    }
  };

  // Direct file upload handler
  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    const formData = new FormData();
    formData.append('file', file);

    // Calculate timeout based on file size
    const fileSizeMB = file.size / (1024 * 1024);
    const timeoutMs = Math.max(60000, 60000 + Math.ceil(fileSizeMB / 5) * 30000);

    try {
      // Simulated progress for upload phase
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 5, 90));
      }, 200);

      const response = await api.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: timeoutMs,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      toast.success('File uploaded successfully!');
      
      // Navigate to cleaning page
      setTimeout(() => {
        navigate(`/clean/${response.data.file_id}`);
      }, 500);

    } catch (error) {
      console.error('Upload failed:', error);
      const message = error.response?.data?.detail || 'Upload failed. Please try again.';
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  }, [navigate]);

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setUploadError(null);
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-invalid-type') {
        setUploadError('Please upload an Excel (.xlsx, .xls) or CSV file');
      } else if (rejection.errors[0]?.code === 'file-too-large') {
        setUploadError('File is too large. Maximum size is 50MB');
      }
      return;
    }
    
    if (acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles[0]);
    }
  }, [handleFileUpload]);

  const { getRootProps, getInputProps, isDragActive: dropzoneActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: false,
    disabled: uploading
  });

  useEffect(() => {
    setIsDragActive(dropzoneActive);
  }, [dropzoneActive]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNewProject = () => {
    navigate('/upload');
  };

  const handleDownload = async (project, format) => {
    try {
      // Check if project has a cleaning job
      if (!project.last_job_id) {
        toast.error('No cleaned data available. Please clean the file first.');
        return;
      }
      
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      
      const response = await api.post('/api/export', {
        job_id: project.last_job_id,
        format
      }, {
        responseType: 'blob'
      });
      
      const filename = `${project.name.replace(/\.[^/.]+$/, '')}_cleaned.${format}`;
      downloadFromResponse(response.data, filename, format);
      
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 403) {
        toast.error('Export limit reached. Please upgrade your plan.');
      } else if (error.response?.status === 404) {
        toast.error('Cleaned data not found. Please clean the file first.');
      } else {
        toast.error('Failed to download file');
      }
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/api/projects/${projectId}`);
      toast.success('Project deleted');
      setProjects(projects.filter(p => p.project_id !== projectId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete project');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <CreditsBadge />
              <Link to="/tools/split">
                <Button 
                  variant="outline"
                  className="text-slate-700 border-slate-300 hover:bg-slate-100"
                  data-testid="split-file-btn"
                >
                  <Scissors className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Split File</span>
                </Button>
              </Link>
              <Button 
                onClick={handleNewProject}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                data-testid="new-project-btn"
              >
                <Plus className="w-4 h-4 sm:mr-2" />
                <Brush className="w-4 h-4 sm:hidden" />
                <span className="hidden sm:inline">New Project</span>
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-10 h-10 rounded-full p-0" data-testid="user-menu-btn">
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-indigo-600" />
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-slate-900">{user?.name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="account-btn">
                    <User className="w-4 h-4 mr-2" />
                    Account & Billing
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setReferralModalOpen(true)} data-testid="referral-btn">
                    <Gift className="w-4 h-4 mr-2 text-emerald-600" />
                    <span className="text-emerald-700">Invite & Earn</span>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-btn">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/dashboard')} data-testid="admin-dashboard-btn">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/lead-gen')} data-testid="admin-leads-btn">
                        <Users className="w-4 h-4 mr-2" />
                        Lead Generation
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/blog-manager')} data-testid="admin-blog-btn">
                        <FileText className="w-4 h-4 mr-2" />
                        Blog Manager
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/use-case-manager')} data-testid="admin-use-case-btn">
                        <Target className="w-4 h-4 mr-2" />
                        Use Case Manager
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={() => setSupportDialogOpen(true)} data-testid="support-btn">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Support
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome + Upload Section - Above the Fold */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Welcome Text */}
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">
                {isNewUser 
                  ? `Welcome, ${user?.name?.split(' ')[0] || 'there'}!` 
                  : `Welcome back, ${user?.name?.split(' ')[0] || 'there'}!`
                }
              </h1>
              <p className="text-slate-600 mb-4">
                {isNewUser 
                  ? "You've got 100 free credits to start. Drop a file below to clean your data."
                  : "Drop a file below to start cleaning your data for CRM import."
                }
              </p>
              
              {/* Quick Tips for New Users */}
              {isNewUser && user?.plan === 'free' && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-4">
                  <h3 className="font-semibold text-indigo-900 text-sm mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    How it works
                  </h3>
                  <ol className="text-sm text-indigo-700 space-y-1">
                    <li>1. Upload your CSV or Excel file</li>
                    <li>2. Select which columns you want to clean</li>
                    <li>3. Review and download your clean data</li>
                  </ol>
                </div>
              )}
            </div>

            {/* Primary Upload Zone - Always Visible */}
            <div className="flex-1 max-w-xl">
              {isMobile ? (
                /* Mobile Message - Upload blocked on mobile */
                <div 
                  className="relative p-8 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50"
                  data-testid="mobile-upload-blocked"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-indigo-100 text-indigo-600">
                      <Monitor className="w-8 h-8" />
                    </div>
                    <p className="text-lg font-semibold text-slate-900 mb-2">
                      Desktop Required
                    </p>
                    <p className="text-sm text-slate-600 mb-4 max-w-xs mx-auto">
                      Our data cleaning tool works best on desktop. Please switch to a computer to upload and clean your files.
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                      <Monitor className="w-4 h-4" />
                      Open on desktop to continue
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop Upload Zone */
                <div
                  {...getRootProps()}
                  className={`
                    relative p-8 rounded-2xl border-2 border-dashed transition-all cursor-pointer
                    ${showUploadGlow 
                      ? 'border-indigo-500 bg-indigo-50 ring-4 ring-indigo-300 ring-opacity-50 animate-pulse shadow-lg shadow-indigo-200' 
                      : uploading 
                      ? 'border-indigo-300 bg-indigo-50' 
                      : isDragActive 
                      ? 'border-indigo-500 bg-indigo-50 scale-[1.02]' 
                      : 'border-slate-300 bg-white hover:border-indigo-400 hover:bg-indigo-50/50'
                    }
                  `}
                  data-testid="upload-dropzone"
                >
                  <input {...getInputProps()} data-testid="file-input" />
                  
                  {/* Glow prompt for new users */}
                  {showUploadGlow && !uploading && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-full shadow-lg animate-bounce">
                      Start here! 👆
                    </div>
                  )}
                  
                  {uploading ? (
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-indigo-600 mx-auto mb-4 animate-spin" />
                      <p className="text-lg font-semibold text-slate-900 mb-2">Uploading...</p>
                      <Progress value={uploadProgress} className="h-2 mb-2" />
                      <p className="text-sm text-slate-500 mb-4">{uploadProgress}% complete</p>
                      
                      {/* Data Hygiene Tips */}
                      <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                        <p className="text-xs text-indigo-600 font-medium mb-1">💡 Did you know?</p>
                        <DataHygieneTip />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-colors ${
                        isDragActive || showUploadGlow ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-600'
                      }`}>
                        <CloudUpload className="w-8 h-8" />
                      </div>
                      <p className="text-lg font-semibold text-slate-900 mb-1">
                        {isDragActive ? 'Drop your file here' : 'Drop a CSV or Excel file here'}
                      </p>
                      <p className="text-sm text-slate-500 mb-4">
                        or click to browse (max 50MB)
                      </p>
                      <Button 
                        variant="outline" 
                        className="rounded-full pointer-events-none"
                        tabIndex={-1}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Browse Files
                      </Button>
                    </div>
                  )}
                  
                  {uploadError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {uploadError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lifetime Time Saved Meter - Show only if user has cleaned files */}
        {aggregateMetrics && aggregateMetrics.total_files_cleaned > 0 && (
          <div className="mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-600" />
                Your AI Data Cleaner Impact
              </h3>
              <div className="relative group">
                <HelpCircle className="w-4 h-4 text-emerald-500 hover:text-emerald-700 cursor-help" />
                <div className="absolute bottom-full right-0 mb-2 w-72 p-4 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl">
                  <p className="font-semibold mb-2">How is this calculated?</p>
                  <p className="text-slate-300 mb-2">
                    Based on the time an Excel expert would spend writing formulas and manually auditing each row for the cleaning tasks you applied.
                  </p>
                  <p className="text-slate-400 text-[10px] mb-2">
                    Time weights per task: Phone Format (12.5s), Dropdown Standardize (12.5s), Revenue (7s), Gender (4s), Email Fix (3.5s), etc.
                  </p>
                  <div className="border-t border-slate-700 pt-2">
                    <p className="text-amber-300 flex items-center gap-1">
                      <span>🛡️</span> 34% conservative reduction applied for accuracy
                    </p>
                  </div>
                  <div className="absolute top-full right-4 border-4 border-transparent border-t-slate-900" />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-2xl font-bold text-emerald-600">
                  {(() => {
                    // Use actual time saved from new Excel Expert model
                    const seconds = aggregateMetrics.total_time_saved_seconds || 0;
                    const hours = seconds / 3600;
                    const minutes = seconds / 60;
                    const days = hours / 8;
                    if (days >= 1) return `${Math.round(hours)}h`;
                    if (hours >= 1) return `${hours.toFixed(1)}h`;
                    if (minutes >= 1) return `${Math.round(minutes)}m`;
                    return '0m';
                  })()}
                </p>
                <p className="text-xs text-slate-500">Time Saved</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-2xl font-bold text-indigo-600">
                  {(() => {
                    // Use total_user_elapsed_time (upload to apply), fallback to processing time
                    const secs = aggregateMetrics.total_user_elapsed_time_seconds || aggregateMetrics.total_processing_time_seconds || 0;
                    if (secs === 0) return '0s';
                    if (secs < 60) return `${Math.round(secs)}s`;
                    const mins = Math.floor(secs / 60);
                    const remainSecs = Math.round(secs % 60);
                    if (mins >= 60) {
                      const hrs = Math.floor(mins / 60);
                      const remainMins = mins % 60;
                      return `${hrs}h ${remainMins}m`;
                    }
                    return remainSecs > 0 ? `${mins}m ${remainSecs}s` : `${mins}m`;
                  })()}
                </p>
                <p className="text-xs text-slate-500">Actual Time Spent</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">
                  {aggregateMetrics.total_files_cleaned.toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Files Cleaned</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">
                  {aggregateMetrics.total_rows_processed.toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Rows Processed</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-2xl font-bold text-slate-900">
                  {(aggregateMetrics.total_cells_cleaned || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Cells Updated</p>
              </div>
            </div>
          </div>
        )}

        {/* Recent Projects */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-slate-900">
              Recent Projects
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : projects.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
              <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No projects yet</h3>
              <p className="text-slate-500 mb-2">Upload your first file using the drop zone above to get started.</p>
              <p className="text-sm text-slate-400">Supported formats: CSV, XLS, XLSX (max 50MB)</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {projects.map((project) => (
                <div 
                  key={project.project_id}
                  className={`group bg-white border rounded-xl p-6 hover:shadow-md transition-all ${
                    project.status === 'completed' 
                      ? 'border-emerald-200 hover:border-emerald-300' 
                      : project.status === 'pending'
                      ? 'border-amber-200 hover:border-amber-300'
                      : project.status === 'failed'
                      ? 'border-red-200 hover:border-red-300'
                      : 'border-slate-200 hover:border-indigo-200'
                  }`}
                  data-testid={`project-card-${project.project_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Icon based on status */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        project.status === 'completed' 
                          ? 'bg-emerald-100' 
                          : project.status === 'pending'
                          ? 'bg-amber-100'
                          : project.status === 'failed'
                          ? 'bg-red-100'
                          : 'bg-slate-100'
                      }`}>
                        {project.status === 'completed' ? (
                          <CheckCircle className="w-6 h-6 text-emerald-600" />
                        ) : project.status === 'pending' ? (
                          <AlertCircle className="w-6 h-6 text-amber-600" />
                        ) : project.status === 'failed' ? (
                          <XCircle className="w-6 h-6 text-red-600" />
                        ) : (
                          <File className="w-6 h-6 text-slate-600" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{project.name}</h3>
                          {/* Status badge */}
                          {project.status === 'completed' ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                              Completed
                            </span>
                          ) : project.status === 'pending' ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                              Ready to Clean
                            </span>
                          ) : project.status === 'failed' ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                              Cleaning Failed
                            </span>
                          ) : null}
                        </div>
                        
                        {/* File info with timestamp */}
                        {project.file && (
                          <p className="text-sm text-slate-500 mb-2">
                            {project.file.name} • {project.file.row_count?.toLocaleString()} rows • {project.file.column_count} columns
                            {project.created_at && (
                              <span className="text-slate-400">
                                {' '} • {new Date(project.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                          </p>
                        )}
                        
                        {/* Stats for completed projects */}
                        {project.status === 'completed' && project.cleaning_stats && (
                          <div className="flex flex-wrap items-center gap-4 mt-3 p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-emerald-500" />
                              <span className="text-sm text-slate-600">
                                <span className="font-semibold text-slate-900">
                                  {project.cleaning_stats.rows_cleaned?.toLocaleString()}
                                </span> rows cleaned
                              </span>
                            </div>
                            {/* Time Saved */}
                            {project.cleaning_stats.time_saved && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-emerald-500" />
                                <span className="text-sm text-slate-600">
                                  <span className="font-semibold text-emerald-600">
                                    ~{project.cleaning_stats.time_saved.total_hours >= 1 
                                      ? `${project.cleaning_stats.time_saved.total_hours}h`
                                      : `${Math.max(1, Math.round(project.cleaning_stats.time_saved.total_minutes))}m`
                                    }
                                  </span> saved
                                </span>
                              </div>
                            )}
                            {/* Credits consumed */}
                            {project.credit_usage && project.credit_usage.credits_consumed > 0 && (
                              <div className="flex items-center gap-2">
                                <Coins className="w-4 h-4 text-indigo-500" />
                                <span className="text-sm text-slate-600">
                                  <span className="font-semibold text-indigo-600">
                                    {project.credit_usage.credits_consumed?.toLocaleString()}
                                  </span> credits used
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Prompt to clean for pending projects */}
                        {project.status === 'pending' && project.file && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                            <p className="text-sm text-amber-700">
                              File uploaded but not yet cleaned. Resume cleaning to process your data.
                            </p>
                          </div>
                        )}
                        
                        {/* Failed cleaning - show File Splitter CTA */}
                        {project.status === 'failed' && project.file && (
                          <div className="mt-3 p-4 bg-red-50 border border-red-100 rounded-lg" data-testid="failed-cleaning-panel">
                            <p className="text-sm text-red-700 mb-3">
                              Cleaning failed{project.file.row_count > 50000 ? ' - file may be too large' : ''}. 
                              Try splitting your file into smaller parts.
                            </p>
                            <Button
                              onClick={() => window.open('/tools/split', '_blank')}
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
                              data-testid="file-splitter-cta-btn"
                            >
                              <Scissors className="w-4 h-4 mr-2" />
                              Split File into Smaller Parts
                              <ExternalLink className="w-3 h-3 ml-2" />
                            </Button>
                            <p className="text-xs text-slate-500 mt-2">
                              Free tool • Supports up to 50MB • Download as ZIP
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-sm text-slate-400 mt-2">
                          <Clock className="w-4 h-4" />
                          <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Resume/Clean button for pending projects */}
                      {project.status === 'pending' && project.file && (
                        <Button
                          onClick={() => navigate(`/clean/${project.file.file_id}`)}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          size="sm"
                          data-testid={`resume-cleaning-${project.project_id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Resume Cleaning
                        </Button>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="w-8 h-8" data-testid={`project-menu-${project.project_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {project.status === 'pending' && project.file && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => navigate(`/clean/${project.file.file_id}`)}
                              >
                                <Play className="w-4 h-4 mr-2" />
                                Resume Cleaning
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          {project.status === 'completed' && (
                            <>
                              <DropdownMenuItem 
                                onClick={() => handleDownload(project, 'csv')}
                                data-testid={`download-csv-${project.project_id}`}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download CSV
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDownload(project, 'xlsx')}
                                data-testid={`download-xlsx-${project.project_id}`}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download Excel
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeleteProject(project.project_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Project
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* Floating Support Button */}
      <SupportButton isOpen={supportDialogOpen} setIsOpen={setSupportDialogOpen} />
      
      {/* Referral Modal */}
      <ReferralModal isOpen={referralModalOpen} onClose={() => setReferralModalOpen(false)} />
    </div>
  );
};

export default DashboardPage;
